import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../../../supabase-client';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Image from '../AppImage';

// Light color class generator for type_thing badges (same palette as AdminThingCard)
const getTypeThingLightColorClass = (typeThing) => {
  if (!typeThing) return 'bg-gray-100 text-gray-800';
  const lightColors = [
    'bg-purple-100 text-purple-800', 'bg-orange-100 text-orange-800', 'bg-red-100 text-red-800', 'bg-pink-100 text-pink-800',
    'bg-indigo-100 text-indigo-800', 'bg-yellow-100 text-yellow-800', 'bg-teal-100 text-teal-800', 'bg-rose-100 text-rose-800',
    'bg-violet-100 text-violet-800', 'bg-amber-100 text-amber-800', 'bg-cyan-100 text-cyan-800', 'bg-lime-100 text-lime-800'
  ];
  let hash = 0;
  for (let i = 0; i < typeThing.length; i++) {
    const char = typeThing.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  const index = Math.abs(hash) % lightColors.length;
  return lightColors[index];
};

const SearchModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Focus on search input when modal opens
      setTimeout(() => {
        document.getElementById('modal-search-input')?.focus();
      }, 100);
    } else {
      // Clear search when modal closes
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [isOpen]);

  const handleSearch = async (query) => {
    const trimmedQuery = query?.trim();
    
    if (!trimmedQuery) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    
    try {
      console.log('🔍 SearchModal: Iniciando búsqueda para:', trimmedQuery);
      
      // Función para normalizar texto (quitar acentos y convertir a minúsculas)
      const normalizeText = (text) => {
        if (!text) return '';
        return text
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, ''); // Remover acentos
      };

      // Búsqueda con todos los campos necesarios incluyendo imágenes
      const [booksResults, biblesResults, thingsResults] = await Promise.all([
        // Buscar libros con autor e imagen
        supabase
          .from('productbook')
          .select(`
            id_product,
            title_product,
            price_original_product,
            image_url_product,
            categories_product,
            authorsbook(name_author)
          `)
          .limit(50),
        
        // Buscar biblias con imagen
        supabase
          .from('biblebook')
          .select(`
            id_bible,
            title_bible,
            price_bible,
            image_urls,
            version_bible
          `)
          .limit(50),
        
        // Buscar accesorios con imagen
        supabase
          .from('thingproduct')
          .select(`
            id_thing,
            title_thing,
            price_thing,
            image_urls,
            type_thing
          `)
          .limit(50)
      ]);

      console.log('📚 SearchModal - Resultados libros:', booksResults.data?.length || 0, booksResults.error);
      console.log('📖 SearchModal - Resultados biblias:', biblesResults.data?.length || 0, biblesResults.error);
      console.log('🎁 SearchModal - Resultados accesorios:', thingsResults.data?.length || 0, thingsResults.error);

      // Mostrar datos crudos para debugging
      console.log('📊 Datos libros:', booksResults.data);
      console.log('📊 Datos biblias:', biblesResults.data);
      console.log('📊 Datos accesorios:', thingsResults.data);

      if (booksResults.error || biblesResults.error || thingsResults.error) {
        console.error('Error en búsqueda:', booksResults.error || biblesResults.error || thingsResults.error);
        return;
      }

      // Filtrar resultados con búsqueda normalizada
      const queryNormalized = normalizeText(trimmedQuery);
      
      const allResults = [
        // Procesar libros con filtrado normalizado
        ...(booksResults.data || [])
          .filter(book => {
            const titleMatch = normalizeText(book.title_product || '').includes(queryNormalized);
            const authorMatch = normalizeText(book.authorsbook?.name_author || '').includes(queryNormalized);
            return titleMatch || authorMatch;
          })
          .slice(0, 5)
          .map(book => ({
            product_id: book.id_product,
            product_title: book.title_product,
            product_price: book.price_original_product,
            product_image: book.image_url_product?.[0] || null,
            product_categories: book.categories_product,
            author_name: book.authorsbook?.name_author || 'Autor desconocido',
            product_type: 'book',
            product_type_label: 'Libro',
            product_type_color: 'bg-blue-100 text-blue-700'
          })),
        
        // Procesar biblias con filtrado normalizado
        ...(biblesResults.data || [])
          .filter(bible => {
            const titleMatch = normalizeText(bible.title_bible || '').includes(queryNormalized);
            const versionMatch = normalizeText(bible.version_bible || '').includes(queryNormalized);
            return titleMatch || versionMatch;
          })
          .slice(0, 5)
          .map(bible => ({
            product_id: bible.id_bible,
            product_title: bible.title_bible,
            product_price: bible.price_bible,
            product_image: bible.image_urls?.[0] || null,
            product_categories: [],
            author_name: bible.version_bible || 'Versión desconocida',
            product_type: 'bible',
            product_type_label: 'Biblia',
            product_type_color: 'bg-green-100 text-green-700'
          })),
        
        // Procesar accesorios con filtrado normalizado
        ...(thingsResults.data || [])
          .filter(thing => {
            const titleMatch = normalizeText(thing.title_thing || '').includes(queryNormalized);
            return titleMatch;
          })
          .slice(0, 5)
          .map(thing => ({
            product_id: thing.id_thing,
            product_title: thing.title_thing,
            product_price: thing.price_thing,
            product_image: thing.image_urls?.[0] || null,
            product_categories: [],
            author_name: 'Accesorio',
            product_type: 'thing',
            product_type_label: thing.type_thing || 'Accesorio',
            product_type_color: getTypeThingLightColorClass(thing.type_thing)
          }))
      ];
      
      console.log('🎯 SearchModal - Total resultados combinados:', allResults.length);
      console.log('📋 SearchModal - Resultados finales:', allResults);
      setSearchResults(allResults);
    } catch (err) {
      console.error('Error inesperado en búsqueda:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (value) => {
    setSearchQuery(value);
    handleSearch(value);
  };

  // Función para normalizar texto (quitar acentos y convertir a minúsculas)
  const normalizeText = (text) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, ''); // Remover acentos
  };

  // Función para resaltar texto coincidente (insensible a acentos y mayúsculas)
  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const normalizedSearchTerm = normalizeText(searchTerm);
    const normalizedText = normalizeText(text);
    
    // Encontrar coincidencias en el texto normalizado
    const startIndex = normalizedText.indexOf(normalizedSearchTerm);
    if (startIndex === -1) return text;
    
    // Usar las posiciones del texto normalizado para resaltar el texto original
    const beforeMatch = text.substring(0, startIndex);
    const match = text.substring(startIndex, startIndex + normalizedSearchTerm.length);
    const afterMatch = text.substring(startIndex + normalizedSearchTerm.length);
    
    return (
      <>
        {beforeMatch}
        <span className="bg-orange-200 text-orange-800 px-1 rounded">
          {match}
        </span>
        {afterMatch}
      </>
    );
  };

  const handleProductClick = (product) => {
    // Navigate to appropriate detail screen based on product type
    if (product.product_type === 'book') {
      navigate(`/product-detail?id=${product.product_id}`);
    } else if (product.product_type === 'bible') {
      navigate(`/bible-detail?id=${product.product_id}`);
    } else if (product.product_type === 'thing') {
      navigate(`/thing-detail?id=${product.product_id}`);
    }
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-24">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-popover border border-border rounded-lg shadow-modal max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-popover-foreground">
            Buscar Productos
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
          >
            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Search Input */}
        <div className="p-4 border-b border-border">
          <div className="relative">
            <Input
              id="modal-search-input"
              type="search"
              placeholder="Buscar libros, biblias y accesorios por título o autor..."
              value={searchQuery}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full pl-10 pr-4 py-3"
            />
            <Icon
              name="Search"
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {isSearching ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Buscando...</span>
            </div>
          ) : searchQuery.trim() === '' ? (
            <div className="text-center py-8">
              <Icon name="Library" size={48} className="mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">
                Escribe para buscar productos por título o autor
              </p>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-2">
              {searchResults.map((product) => (
                <button
                  key={product.product_id}
                  onClick={() => handleProductClick(product)}
                  className="w-full flex items-center gap-4 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                >
                  <Image
                    src={product.product_image || '/assets/images/book-placeholder.png'}
                    alt={product.product_title}
                    className="w-12 h-16 object-cover rounded flex-shrink-0"
                    low
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-popover-foreground truncate">
                        {highlightText(product.product_title, searchQuery.trim())}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${product.product_type_color}`}>
                        {product.product_type_label}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      por {highlightText(product.author_name || 'Autor desconocido', searchQuery.trim())}
                    </p>
                    <p className="text-sm font-semibold text-primary mt-1">
                      ${product.product_price}
                    </p>
                  </div>
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Icon name="Search" size={48} className="mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">
                No se encontraron resultados para "{searchQuery}"
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Intenta con otros términos de búsqueda
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;

